import React from "react";
import { tWithKeyAttr as t } from "../../../../i18nHelper";
import styles from "../../appointmentsPage.module.css";
import { IAppointment } from "../../types/appointments.types";
import { getAppointmentColors, truncateText } from "../../utils/fundingSourceColors";

interface IAppointmentCardProps {
  appointment: IAppointment;
  onClick: () => void;
}

/**
 * Appointment Card Component - Client Version
 * 
 * Displays a single appointment with therapist name, time, and type/status
 */
const AppointmentCard: React.FC<IAppointmentCardProps> = ({ appointment, onClick }) => {
  const isGoogleEvent = appointment.source === 'google_calendar';
  const isOther = appointment.source === 'other' || (!appointment.therapistsId && !appointment.therapistId && !appointment.treatmentGroupName && !appointment.mediaId && !isGoogleEvent);
  
  // Use centralized color logic
  const colors = getAppointmentColors(appointment);
  const borderColor = colors.borderColor;

  return (
    <div 
      className={`${styles.appointmentCard} ${isGoogleEvent ? styles.googleEventCard : ''}`}
      onClick={onClick}
      style={{ borderRightColor: borderColor, backgroundColor: colors.backgroundColor }}
    >
      <div className={styles.appointmentHeader}>
        <div className={styles.appointmentTime}>
          {isGoogleEvent ? "📅 " : ""}{appointment.time}
        </div>
        {appointment.endTime && (
          <div className={styles.appointmentDuration}>
            {appointment.time} - {appointment.endTime}
          </div>
        )}
      </div>
      
      <div className={styles.appointmentDetails}>
        {/* Google Event Summary or Other Event or Therapist Name */}
        {isGoogleEvent ? (
          <div className={styles.appointmentRow}>
            <span className={styles.appointmentIcon}>📍</span>
            <span className={styles.appointmentValue} style={{ fontWeight: 600 }}>
              {truncateText(appointment.patientName)}
            </span>
          </div>
        ) : isOther ? (
          <div className={styles.appointmentRow}>
            <span className={styles.appointmentIcon}>📝</span>
            <span className={styles.appointmentValue} style={{ fontWeight: 600 }}>
              {t("OTHER") || "אחר"}
            </span>
          </div>
        ) : (
          <>
            {!appointment.therapistName && appointment.treatmentGroupName && (
              <div className={styles.appointmentRow}>
                <span className={styles.appointmentIcon}>👥</span>
                <span className={styles.appointmentLabel}>
                  {t("APPOINTMENTS_TREATMENT_GROUP")}:
                </span>
                <span className={styles.appointmentValue} style={{ color: '#10B981', fontWeight: 500 }}>
                  {truncateText(appointment.treatmentGroupName)}
                </span>
              </div>
            )}
            <div className={styles.appointmentRow}>
              <span className={styles.appointmentIcon}>👨‍⚕️</span>
              <span className={styles.appointmentLabel}>{t("THERAPIST_NAME")}:</span>
              <span className={styles.appointmentValue}>{truncateText(appointment.therapistName)}</span>
            </div>
          </>
        )}

        {/* Location for Google Events */}
        {isGoogleEvent && appointment.location && (
          <div className={styles.appointmentRow}>
            <span className={styles.appointmentIcon}>🏘️</span>
            <span className={styles.appointmentValue}>{appointment.location}</span>
          </div>
        )}

        {/* Comments/Content for Other Events */}
        {isOther && appointment.comments && (
          <div className={styles.appointmentRow}>
            <span className={styles.appointmentIcon}>💬</span>
            <span className={styles.appointmentValue} style={{ 
              fontSize: '12px', 
              color: '#4B5563',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden'
            }}>
              {appointment.comments}
            </span>
          </div>
        )}

        {/* Funding Source / Status Badge */}
        {!isGoogleEvent && !isOther && appointment.fundingSourceName && (
          <div className={styles.appointmentRow}>
            <span className={styles.appointmentIcon}>💳</span>
            <span className={styles.appointmentLabel}>{t("APPOINTMENTS_FUNDING")}:</span>
            <span 
              className={styles.fundingBadge}
              style={{ 
                backgroundColor: colors.badgeBg,
                color: colors.textColor
              }}
            >
              {appointment.fundingSourceName}
            </span>
          </div>
        )}

        {/* Google badge for google events */}
        {isGoogleEvent && (
          <div className={styles.appointmentRow}>
            <span 
              className={styles.fundingBadge}
              style={{ 
                backgroundColor: 'rgba(66, 133, 244, 0.1)',
                color: '#4285F4',
                fontSize: '11px'
              }}
            >
              Google Calendar
            </span>
          </div>
        )}

        {/* Treatment Type */}
        {!isGoogleEvent && !isOther && appointment.treatmentTypeName && (
          <div className={styles.appointmentRow}>
            <span className={styles.appointmentIcon}>🏥</span>
            <span className={styles.appointmentLabel}>{t("APPOINTMENTS_TREATMENT_TYPE")}:</span>
            <span className={styles.appointmentValue}>{appointment.treatmentTypeName}</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default AppointmentCard;
